/** The marks the transition pathway draws, from stored figures only.
 *
 * Its own module so a test can assert what would be drawn without rendering, and so the component
 * file exports only a component. See `TransitionPathway.tsx` for why every mark is a stored figure
 * and why nothing is interpolated.
 */

import type { PlanTarget } from "../api/queries";

export interface Mark {
  key: string;
  kind: "baseline" | "commitment" | "modelled";
  year: number;
  amount: string;
  unit: string;
  label: string;
  basis: string;
}

export function pathwayMarks(
  target: PlanTarget,
  projections: ReadonlyArray<{
    assumption_version: number;
    emissions: string | null;
    unit: string | null;
    horizon_year: number;
    refusal: string | null;
  }>,
): Mark[] {
  const marks: Mark[] = [
    {
      key: "baseline",
      kind: "baseline",
      year: target.baseline_year,
      amount: target.baseline_amount,
      unit: target.baseline_unit,
      label: `Baseline, ${target.baseline_year}`,
      basis: "measured",
    },
    {
      key: "commitment",
      kind: "commitment",
      year: target.horizon_year,
      amount: target.implied_target,
      unit: target.baseline_unit,
      label: `Committed level, ${target.horizon_year}`,
      basis: "derived from the commitment",
    },
  ];
  for (const projection of projections) {
    // A refused projection has no figure, and a mark for it would be a guess.
    if (projection.refusal || projection.emissions === null || projection.unit === null) continue;
    marks.push({
      key: `modelled-${projection.assumption_version}`,
      kind: "modelled",
      year: projection.horizon_year,
      amount: projection.emissions,
      unit: projection.unit,
      label: `Assumptions version ${projection.assumption_version}, ${projection.horizon_year}`,
      basis: "modelled, not measured",
    });
  }
  return marks;
}
